/**
 *
 * MobileParentHeader
 *
 */

import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import { compose } from "redux";
import classNames from "classnames";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// antd component
// import { Button } from 'antd';

// assets
import CustomIcons from "components/CustomIcons";

// components
import ChildPreview from "mobile/components/ChildPreview";

import history from "utils/history";

// global user
import { withUser } from "engine/Contexts/User.context";

// hooks
import { useAuthRole } from "utils/hooks";
import _local from "utils/localStorageUtils";

function MobileParentHeader({
  visibility,
  collapse,
  roles,
  user,
  // default props
  className,
}) {
  const { t, i18n } = useTranslation();
  const localUser = _local.getLocalUser();
  const roleClass = localUser?.role ? localUser?.role : "";
  const language = localStorage.getItem('i18nextLng') || process.env.DEFAULT_LANGUAGE || "fr";

  const location = useLocation();
  const containerRef = useRef(null);
  const activeItemRef = useRef(null);

  const [studentsNotif, setStudentsNotif] = useState(false);
  const [studentList, setStudentList] = useState([]);
  const [activeChild, setActiveChild] = useState(false);

  const [touchStartX, setTouchStartX] = useState(0);
  const [touchEndX, setTouchEndX] = useState(0);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [maxScrollPosition, setMaxScrollPosition] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [loading, setLoading] = useState(false);

  const MIN_DISTANCE = 5;
  const CONTAINER_PADDING = 15;
  const CONTAINER_SCROLL_STEP = !collapse ? 100 : 150;

  const childClassNames = classNames(
    "layout-header-parent",
    roleClass,
    className,
  );

  // INFO: disable ability to change language based on school language, latest changes from client on 02.2024
  /* const chageLanguage = useCallback(lang => { 
    if (lang != i18n.language) { 
      i18n.changeLanguage(!!lang && lang || language); 
    }
  }, [i18n.language, language]); */

  const changeActiveChild = useCallback((child) => { 
    if (!!child && child?._id != activeChild?._id) { 
      setActiveChild(child);
      // INFO: disable ability to change language based on school language, latest changes from client on 02.2024
      //chageLanguage(child?.school?.language);
   //   _local.setLocalSchool(child?.school);
    }
  }, [i18n.language, language, activeChild]);

  /* Load scools and set active child on first load */
  useEffect(() => {
    // active item
    const localActiveStd = _local.getLocalChild();
    const std = (!!localActiveStd && localActiveStd) || user?.students?.[0];
    changeActiveChild(std);
    // list
    setStudentList(user?.students);
  }, []);

  /* Load scools and set active child if students list inside context is chaged */
  useEffect(() => {
    // active item
    if (!!!activeChild) {
      const localActiveStd = _local.getLocalChild();
      const std = (!!localActiveStd && localActiveStd) || user?.students?.[0];
      changeActiveChild(std);
    }

    // reset studetns list only if we have logging user
    if (!!user?._id) {
      const newStudants = studentList?.length > 0 ? user?.students?.filter(std => !studentList?.find(el => el?._id == std?._id)) : user?.students;
      if (newStudants?.length > 0) {
        setStudentList([...studentList, ...newStudants]);
      } else {
        setStudentList(studentList?.map(std => {
          const findStd = user?.students?.find(el => el?._id == std?._id)
          if (!!findStd) {
            return findStd
          } else {
            return std
          }
        }));
      }
    }

  }, [user?.students]);

  useEffect(() => {
    setStudentsNotif(user?.childsNotifications);
  }, [user?.childsNotifications]);

  // If pathname is changes than we scroll to the active list item
  useEffect(() => {
    if (!!containerRef?.current && !!activeItemRef?.current) {
      containerRef.current.scrollTo({
        left: activeItemRef?.current?.offsetLeft - CONTAINER_PADDING,
        behavior: "smooth",
      });
    }
  }, [location?.pathname]);

  // Init scrollbar container and set max positin for scrolling
  useEffect(() => {
    if (!!containerRef?.current) {
      setMaxScrollPosition(
        containerRef?.current?.scrollWidth - containerRef?.current?.clientWidth,
      );
      return () => {
        setIsDragging(false);
      };
    }
  }, [containerRef]);

  useEffect(() => {
    setIsDragging(loading);
  }, [loading]);

  const handleChildClick = (child, index) => {
    setLoading(true);
    user.changeChildByID(child?._id)
      .then(result => {
        changeActiveChild(result || child);
        setLoading(false);
      })
      .catch(err => {
        console.error(err);
        changeActiveChild(child);
        setLoading(false);
      });
  };

  const onMountedItemList = useCallback(
    ref => {
      activeItemRef.current = ref?.current || null;
      if (!!containerRef?.current && !!ref?.current && !isDragging) {
        containerRef.current.scrollTo({
          left: ref?.current?.offsetLeft - CONTAINER_PADDING,
          behavior: "smooth",
        });
      }
    },
    [activeItemRef, isDragging],
  );
  const handleContainerScroll = e => {
    setIsDragging(true);
    setScrollPosition(e.target.scrollLeft);
    setMaxScrollPosition(e.target.scrollWidth - e.target.clientWidth);
  };
  const handleTouchStart = e => {
    setIsDragging(false);
    const touchStart = e.touches[0].clientX;
    setTouchStartX(touchStart);
  };
  const handleTouchMove = e => {
    setIsDragging(true);
    const touchEnd = e.touches[0].clientX;
    setTouchEndX(touchEnd);
  };
  const handleTouchEnd = e => {
    // const distance = e.changedTouches[0].clientX - touchStartX;
    const distance = touchEndX - touchStartX;
    if (distance < MIN_DISTANCE) {
      // click
      // console.log("Click", isDragging, distance);
    } else {
      // swipe
      // console.log("Swipe", isDragging, distance);
      if (distance > 0) {
        !!containerRef?.current &&
          containerRef?.current.scrollTo({
            left: scrollPosition - CONTAINER_SCROLL_STEP,
            behavior: "smooth",
          });
      } else {
        !!containerRef?.current &&
          containerRef?.current.scrollTo({
            left: scrollPosition + CONTAINER_SCROLL_STEP,
            behavior: "smooth",
          });
      }
      setIsDragging(false);
    }
  };
  const handleArrowClick = direction => {
    setIsDragging(true);
    if (direction === "left") {
      !!containerRef?.current &&
        containerRef?.current.scrollTo({
          left: scrollPosition - CONTAINER_SCROLL_STEP,
          behavior: "smooth",
        });
    } else {
      !!containerRef?.current &&
        containerRef?.current.scrollTo({
          left: scrollPosition + CONTAINER_SCROLL_STEP,
          behavior: "smooth",
        });
    }
    setIsDragging(false);
  };

  const addNewChild = useMemo(
    () => (
      <div
        className="add-new-child child-qr"
        onClick={() => {
          !isDragging &&
            history.push({
              pathname: `/linkstudent`,
            });
        }}
      >
        <CustomIcons type="qrcode" />
      </div>
    ),
    [],
  );

  const renderList = useMemo(() => {
    // console.log("studentList", studentList);
    return studentList?.length > 0 ? (
      <div
        className="mobile-child-menu"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onScroll={handleContainerScroll}
        ref={containerRef}
      >
        {studentList?.map(
          (student, index) =>
            !!student?._id && (
              <ChildPreview
                key={`side-menu-std-info-${student?._id}`}
                view="small"
                showChangeButton={false}
                collapse={collapse}
                loading={loading}
                isActive={student?._id == activeChild?._id}
                child={student}
                notifications={studentsNotif}
                onClick={() => {
                  handleChildClick(student, index);
                }}
                onMount={ref => onMountedItemList(ref)}
              />
            ),
        )}
        {addNewChild}
      </div>
    ) : (
      <div className="mobile-child-menu" ref={containerRef}>
        {addNewChild}
      </div>
    );
  }, [
    activeChild,
    studentList,
    collapse,
    containerRef,
    studentsNotif,
    loading,
  ]);

  return ((
    !!useAuthRole(roles) && !!visibility
  ) && (
      <div
        className={classNames(
          collapse && "collapsed",
          loading && "loading",
          childClassNames,
        )}
      >
        <span
          className={classNames(
            "horizontal-menu-arrow left",
            scrollPosition === 0 && "hide",
          )}
          onClick={() => handleArrowClick("left")}
        />

        {renderList}

        <span
          className={classNames(
            "horizontal-menu-arrow right",
            scrollPosition === maxScrollPosition && "hide",
          )}
          onClick={() => handleArrowClick("right")}
        />
      </div>
    )
  );
}

MobileParentHeader.defaultProps = {
  visibility: true,
  collapse: false,
  roles: ["parent"]
};

MobileParentHeader.propTypes = {};

export default compose(withUser)(MobileParentHeader);
